import React, { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import { PromoNotification } from 'reducers/user';
import { SAFE_AREA_BOTTOM, SAFE_AREA_RIGHT, SAFE_AREA_TOP } from 'utils/safeArea';

import { CashierContext } from 'components/Cashier/CashierContext';

import NotificationList, {
  formatRelativeTime,
  getNotificationType,
  InboxIcon,
  SALOON
} from './NotificationList';
import { useNotifications } from './useNotifications';

const NotificationCenter: React.FC = () => {
  const { sortedNotifications, unreadNotifications, unreadCount, markNotificationRead } =
    useNotifications();
  const { openCashier } = useContext(CashierContext);
  const [open, setOpen] = useState(false);
  const [alertNotification, setAlertNotification] = useState<PromoNotification | null>(null);
  const initializedRef = useRef(false);
  const previousUnreadIdsRef = useRef<number[]>([]);

  useEffect(() => {
    const currentUnreadIds = unreadNotifications.map((notification) => notification.id);

    if (!initializedRef.current) {
      initializedRef.current = true;
      previousUnreadIdsRef.current = currentUnreadIds;
      return;
    }

    const newUnread = unreadNotifications.filter(
      (notification) => !previousUnreadIdsRef.current.includes(notification.id)
    );

    if (newUnread.length > 0) {
      setAlertNotification(newUnread[0]);
    }

    previousUnreadIdsRef.current = currentUnreadIds;
  }, [unreadNotifications]);

  useEffect(() => {
    if (!alertNotification) return;

    const timer = window.setTimeout(() => {
      setAlertNotification(null);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [alertNotification]);

  const handleNotificationClick = (notification: PromoNotification) => {
    setAlertNotification((current) => (current?.id === notification.id ? null : current));
    void markNotificationRead(notification);

    // Money/cashier notifications (refunds, deposits, withdrawals) deep-link to
    // the cashier's transaction history so the player can see the entry.
    const type = getNotificationType(`${notification.heading || ''} ${notification.message || ''}`);
    if (type === 'cashier') {
      setOpen(false);
      openCashier('history');
    }
  };

  return (
    <>
      <button
        type='button'
        aria-label={
          unreadCount > 0 ? `Open notifications (${unreadCount} unread)` : 'Open notifications'
        }
        onClick={() => setOpen((current) => !current)}
        style={{
          ...triggerButtonStyle,
          color: unreadCount > 0 ? SALOON.orange200 : SALOON.orange300
        }}>
        <InboxIcon />
        {unreadCount > 0 && <span style={badgeStyle} aria-hidden='true' />}
      </button>

      {createPortal(
        <>
          {open && <div style={backdropStyle} onClick={() => setOpen(false)} />}

          {open && (
            <aside style={panelStyle} aria-label='Notifications screen'>
              <div style={panelHeaderStyle}>
                <div style={panelHeadingGroupStyle}>
                  <span style={panelTitleStyle}>Notifications</span>
                  <span style={panelSubtitleStyle}>
                    {unreadCount > 0
                      ? `${unreadCount} unread update${unreadCount > 1 ? 's' : ''}`
                      : 'You are all caught up'}
                  </span>
                </div>

                <button
                  type='button'
                  aria-label='Close notifications'
                  onClick={() => setOpen(false)}
                  style={closeButtonStyle}>
                  <CloseIcon style={{ fontSize: '1rem' }} />
                </button>
              </div>

              <NotificationList
                notifications={sortedNotifications}
                onNotificationClick={handleNotificationClick}
              />
            </aside>
          )}

          {alertNotification && !open && (
            <div
              role='status'
              aria-live='polite'
              style={alertCardStyle}
              onClick={() => {
                setOpen(true);
                setAlertNotification(null);
              }}>
              <div style={alertHeaderStyle}>
                <span style={alertTitleStyle}>New Notification</span>
                <span style={alertTimeStyle}>
                  {formatRelativeTime(alertNotification.timestamp)}
                </span>
              </div>
              <span style={alertMessageStyle}>{alertNotification.message}</span>
              <span style={alertHintStyle}>Tap to review</span>
            </div>
          )}
        </>,
        document.body
      )}
    </>
  );
};

export default NotificationCenter;

const triggerButtonStyle: CSSProperties = {
  position: 'relative',
  width: 42,
  height: 42,
  borderRadius: '50%',
  border: `1px solid ${SALOON.orange500}73`, // ~0.45 alpha
  background: 'rgba(0, 0, 0, 0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: SALOON.orange300,
  flexShrink: 0,
  transition: 'background 0.15s, border-color 0.15s, color 0.15s'
};

// Unread pulse dot — matches the design's .m-icon-btn-dot from Skullcrusher's
// mobile-app spec: 7×7 orange-500 fill, glow halo, navy outline ring so it
// stays readable on the dark header. We dropped the numeric count badge
// intentionally; the design surfaces "unread, here" with the dot and lets the
// user open the panel for the actual count. Screen readers still get the count
// via the button's aria-label.
const badgeStyle: CSSProperties = {
  position: 'absolute',
  top: 6,
  right: 6,
  width: 7,
  height: 7,
  borderRadius: '50%',
  background: SALOON.orange500,
  boxShadow: `0 0 6px rgba(232, 130, 31, 0.8), 0 0 0 1.5px ${SALOON.navy900}`,
  pointerEvents: 'none'
};

const backdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.55)',
  zIndex: 1080
};

const panelStyle: CSSProperties = {
  position: 'fixed',
  // Offset by the safe-area insets so the panel clears the notch/status bar
  // (the header it anchors under is itself pushed down by the top inset) and
  // the right-edge gutter clears a landscape notch. The maxHeight subtracts
  // both vertical insets so a full list never runs under the home indicator.
  top: `calc(78px + ${SAFE_AREA_TOP})`,
  right: `calc(16px + ${SAFE_AREA_RIGHT})`,
  width: 'min(380px, calc(100vw - 24px))',
  maxHeight: `min(72vh, calc(100vh - 94px - ${SAFE_AREA_TOP} - ${SAFE_AREA_BOTTOM}))`,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  background: `linear-gradient(180deg, ${SALOON.navy800}, ${SALOON.navy900})`,
  border: `1px solid ${SALOON.orange500}59`, // ~0.35 alpha
  boxShadow: '0 14px 34px rgba(0,0,0,0.55)',
  overflow: 'hidden',
  zIndex: 1100,
  color: SALOON.bone,
  fontFamily: SALOON.fontBody
};

const panelHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 18px 12px',
  borderBottom: `1px solid ${SALOON.orange500}20` // ~0.12 alpha
};

const panelHeadingGroupStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4
};

const panelTitleStyle: CSSProperties = {
  fontFamily: SALOON.fontDecorative,
  color: SALOON.orange300,
  fontSize: '1.1rem',
  letterSpacing: '0.02em',
  textShadow: '0 2px 0 rgba(0,0,0,0.5)'
};

const panelSubtitleStyle: CSSProperties = {
  fontFamily: SALOON.fontTypewriter,
  color: SALOON.bone2,
  fontSize: '0.7rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  opacity: 0.85
};

const closeButtonStyle: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  border: `1px solid ${SALOON.orange500}73`,
  background: 'rgba(0, 0, 0, 0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: SALOON.orange300,
  cursor: 'pointer',
  flexShrink: 0
};

const alertCardStyle: CSSProperties = {
  position: 'fixed',
  // Match the panel's safe-area handling so the toast clears the notch and the
  // landscape right-edge inset.
  top: `calc(90px + ${SAFE_AREA_TOP})`,
  right: `calc(16px + ${SAFE_AREA_RIGHT})`,
  width: 'min(320px, calc(100vw - 24px))',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: '14px 15px',
  borderRadius: '12px',
  border: `1px solid ${SALOON.orange500}59`,
  background: `linear-gradient(180deg, ${SALOON.navy800}, ${SALOON.navy900})`,
  boxShadow: '0 14px 34px rgba(0,0,0,0.55)',
  zIndex: 1090,
  cursor: 'pointer',
  color: SALOON.bone,
  fontFamily: SALOON.fontBody
};

const alertHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12
};

const alertTitleStyle: CSSProperties = {
  fontFamily: SALOON.fontTypewriter,
  color: SALOON.orange300,
  fontSize: '0.7rem',
  letterSpacing: '0.12em',
  textTransform: 'uppercase'
};

const alertTimeStyle: CSSProperties = {
  fontFamily: SALOON.fontTypewriter,
  color: SALOON.bone2,
  fontSize: '0.66rem',
  opacity: 0.75
};

const alertMessageStyle: CSSProperties = {
  color: SALOON.bone,
  fontSize: '0.84rem',
  lineHeight: 1.4,
  fontWeight: 600
};

const alertHintStyle: CSSProperties = {
  fontFamily: SALOON.fontTypewriter,
  color: SALOON.bone2,
  fontSize: '0.68rem',
  letterSpacing: '0.06em',
  opacity: 0.75
};
